import type { AccountKind, IAccountIdp, IRoleRef, Permission } from '@/domains/auth';
import type { ParticipantKind } from '@/domains/survey';
import type { AccountRelation } from './participant-contracts';

/**
 * Accounts, as survey-api actually answers them — the **real** half of what
 * `contracts.ts` proposed for users.
 *
 * These routes back this file and they do not share a prefix, which is worth knowing before
 * reading `accounts-api.ts`:
 *
 * - `GET /api/v1/admin/accounts` (SR-124) — the list, under `accounts.browse`.
 * - `GET /api/v1/admin/accounts/by-email/{email}` (SR-210) — one account, exactly addressed,
 *   or a 404. Same policy as the list. survey-api ships a second lookup beside it,
 *   `GET …/accounts/{accountId}`, which this app does not call: every screen here arrives
 *   holding an address. The writes below name their target by the id that read answered.
 * - `POST /accounts` — create a seat, under `accounts.manage`; a Team Portal seat asks
 *   `roles.manage` besides. Sits at the **root**, not under `/api/v1`, because it is mapped
 *   beside `/auth` rather than inside the versioned group.
 * - `PUT /api/v1/admin/accounts/{accountId}/roles` and `PATCH /api/v1/admin/accounts/{accountId}`
 *   (SR-228, survey-api's spec §6.2) — an account's Team Portal roles, replaced whole, and its
 *   kind, both `roles.manage` and both by **account id**. They replaced
 *   `POST /accounts/role-grants`, which set the one role by address.
 * - `PUT /api/v1/admin/accounts/{accountId}/status` (SR-442) — an account disabled or made active
 *   again, `accounts.manage`, and `roles.manage` too for a Team Portal account. By account id.
 * - `GET` and `POST /api/v1/admin/accounts/{accountId}/idp-invitations`, and
 *   `DELETE …/accounts/{accountId}/idp-link` (SR-476, IDP §5) — the account's invitations to
 *   sign in at Invent's IdP, one sent, and the identity it signs in with unlinked. The list is
 *   `accounts.browse`; the two writes `accounts.manage`, and `roles.manage` too for a Team Portal
 *   account. By account id.
 *
 * ## What is here, and what is deliberately absent
 *
 * `contracts.ts` predicted this surface before it shipped and got several things wrong.
 * They are worth naming, because a screen written against the prediction would be promising
 * writes no endpoint can perform:
 *
 * - ~~**No detail route.**~~ **SR-210 built it**, and `IAdminAccountDetail` below is what it
 *   answers. The prediction was right about the shape of the gap and wrong about it lasting:
 *   the detail page used to find its row through the list's `?email=` **substring** filter,
 *   which matches `joanna@` for `?email=ann` and gives a client no way to say which row it
 *   meant.
 * - ~~**No `lastSignInAt`.**~~ **SR-210 projects it**, as `activity.lastLoginAt`, out of the
 *   append-only `account_event` rather than out of a session row — so it survives the purge
 *   that clears `activity.lastSeenAt`.
 * - **No `account_event` feed.** The table is written — `CreateAccountService`, the
 *   account-role write and the IdP invitation and unlink record to it — and SR-210 reads one
 *   instant back out of it. The feed itself is read over HTTP by participant alone (SR-215's
 *   activity route, the person hub's Activity), so no write here leaves a trace the user card
 *   can show.
 * - ~~**No participant link editing.**~~ **survey-api's block 6 built it** — but on the
 *   participant, not here: `POST …/participants/{id}/account-links` names the account by
 *   address and `DELETE …/account-links/{accountId}` removes one, both under
 *   `accounts.manage` (staff and admin). They are `participants-api.ts`'s, because they sit
 *   under that prefix, and the person hub is where they are pressed.
 *
 * ## The writes that cause a mail
 *
 * Creating a seat queues its invitation to sign in at the IdP, and staff's invitation queues
 * another; survey-api's worker sends each. Both take `deliverTo`, the test inbox a test
 * environment invites instead of the account — see `useTestInbox` in the auth domain, and the
 * refusals it words — as the address write does. The roles, the status and the unlink mail
 * nothing and take none.
 */

/** `account.status` — a string constant column, not an enum, so it passes through as stored. */
export type AccountStatus = 'active' | 'disabled';

/** The whole vocabulary, mirroring survey-api's `AccountStatus.All`. */
export const ACCOUNT_STATUSES: readonly AccountStatus[] = ['active', 'disabled'];

/** survey-api's `AccountKind`, both values (SR-228) — the order the Kind filter offers them. */
export const ACCOUNT_KINDS: readonly AccountKind[] = ['advisor', 'staff'];

/**
 * One account on the wire — `AdminAccountListItemDto`, field for field.
 *
 * `role` went with SR-228, from survey-api's DTO as from this type: the account is a **kind**
 * and, for staff, any number of roles.
 */
export interface IAdminAccountRow {
  accountId: string;
  email: string;
  kind: AccountKind;
  /**
   * The Team Portal roles the account holds, `{ roleId, code, name }` as survey-api's spec §6.3
   * names them — `[]` for an advisor, and for staff given none yet. Drawn by `name`, in the order
   * sent; never read for authority on this side.
   */
  roles: IRoleRef[];
  status: AccountStatus;
  createdAt: string;
  updatedAt: string;
  /**
   * The display names of the participants this account acts for — names only, no ids.
   *
   * This is also the one filter that reaches past the account table: `?name=` matches these
   * rather than the address, which is why the column has to be rendered for the match to be
   * legible.
   */
  participantNames: string[];
}

/**
 * One participant this account acts for — `AdminAccountParticipantDto`, which is
 * `GET /my/participants`' row under another name.
 *
 * survey-api gave the two surfaces one shape deliberately, so an administrator's view of the
 * links renders with whatever already renders a respondent's. `ParticipantKind` is therefore
 * imported from the survey domain rather than restated here.
 */
export interface IAdminAccountParticipant {
  participantId: string;
  kind: ParticipantKind;
  displayName: string;
  /** `self` — the account's own participant; `contact` — the account is its point of contact,
   *  linked by an accepted invitation (SR-416). An unknown spelling is drawn as spelled. */
  relation: AccountRelation;
  photoUri: Nullable<string>;
}

/**
 * What the account has been doing, in three numbers — which do not share a lifetime, and a
 * screen that renders them as one block has to say so.
 *
 * `lastLoginAt` comes from `account_event`, which is append-only, so it is the durable one.
 * `lastSeenAt` and `activeSessionCount` come from live sessions instead, which
 * `SessionPurgeWorker` deletes — so both fall back to null and 0 on their own. A `disabled`
 * account reports 0: disabling revokes every session (SR-442), and survey-api reads the status
 * on every request besides. A screen wanting "last active" wants `lastLoginAt`.
 */
export interface IAdminAccountActivity {
  lastLoginAt: Nullable<string>;
  lastSeenAt: Nullable<string>;
  activeSessionCount: number;
}

/**
 * One account as an administrator reads it (SR-210) — `AdminAccountDetailDto`.
 *
 * `extends IAdminAccountRow` rather than a restatement of its eight members: survey-api builds
 * this payload as a **strict superset** of the list row — same names, same types — and pins
 * that mechanically, because the way it would break is an edit to the list row by an author
 * with no reason to open the detail. The inheritance is that promise written in the type, so
 * anything rendering a row renders this with no branch.
 *
 * `participantNames` is inherited with the rest and stays true — the server derives it from
 * `participants` rather than reading the link table twice, so the two cannot disagree — but
 * `participants` is the richer of the two and is what a screen with room reaches for.
 */
export interface IAdminAccountDetail extends IAdminAccountRow {
  /** `[]`, never null. Ordered by display name with a participant id tie-break, so two
   *  participants sharing a name cannot order differently between two reads. */
  participants: IAdminAccountParticipant[];
  activity: IAdminAccountActivity;
  /**
   * The identity the account signs in with at Invent's IdP — its address there, which on dev
   * and UAT is the tester's, and when a first sign-in linked it (IDP §5). `null` until one does,
   * and again after staff unlink it.
   */
  idp: Nullable<IAccountIdp>;
  /**
   * When survey-api learned that the owner receives mail at `email` (its self-service plan,
   * R8–R9) — by a redeemed confirmation link, a contact's accepted invitation, or an address
   * change whose link was mailed to the owner. It only ever sets, and gates nothing (R15). No
   * backfill: every migrated account starts null, so null is "not confirmed", never "no
   * address".
   *
   * On the detail and not the list row, as survey-api has it (R16) — the detail is the row's
   * superset, not the reverse.
   */
  emailVerifiedAt: Nullable<string>;
  /**
   * The account's effective permissions, the union of its roles' (SR-228) — on the detail and
   * not the row, as survey-api's spec §6.3 has it. Drawn ("What this account can do"), never
   * compared with the caller's: whether one outranks the other is survey-api's D5. A code this
   * app does not mirror is kept as sent — which is what the type says: a `Permission`, or any
   * other string survey-api adds before this app's mirror moves.
   */
  permissions: (Permission | (string & {}))[];
}

/**
 * The list filter. `kind`, `roleId` and `status` repeat once per value and an unrecognised one
 * is a 400 rather than an empty page — the vocabularies are validated at the edge, and a
 * request wrong in several ways is told all of it at once. `roleId` repeats, as `role` did
 * before it (the role-model UI design's §9, item 8).
 *
 * `email` and `name` are substrings. There is no sort parameter: the order is fixed at
 * `createdAt DESC`, deliberately not alphabetical, because a paged read resolves its order
 * in SQL and a name ordering there is collation-dependent.
 */
export interface IAccountQuery {
  kind?: AccountKind[];
  roleId?: string[];
  status?: AccountStatus[];
  email?: string;
  name?: string;
  page?: number;
  pageSize?: number;
}

/**
 * The test inbox a request that causes a mail may name — survey-api's `deliverTo`. Sent only
 * while the deployment holds its mail to an allowlist, and then required unless the account's
 * own address is at an allowed domain; production refuses it outright. `useTestInbox` builds
 * it, so no call site decides whether to send it.
 */
export interface IDeliverTo {
  deliverTo?: string;
}

/**
 * `CreateAccountRequest`. Answers 201 with **no body** — not even the new id in a readable
 * form, only a `Location` header — so a caller learns nothing it can act on beyond "it
 * worked".
 *
 * A created seat signs in through Invent's IdP: survey-api queues its invitation, and its
 * worker asks the IdP and mails the redeem link — to `deliverTo` where one is named. Refused 409
 * with no body for an address another account holds, 409 `idp-address-taken` for one another
 * account's live invitation or identity holds. If the mail does not arrive, the account's Sign-in
 * card shows the redeem link once the IdP made it.
 */
export interface ICreateAccount extends IDeliverTo {
  email: string;
  kind: AccountKind;
  /**
   * The Team Portal roles the seat starts with — staff only; `[]` or absent is a seat that opens
   * nothing but Team Home yet, which survey-api allows. Giving one asks `roles.manage`, and a role
   * holding a permission the caller lacks is survey-api's 422 `permission-not-held` (D5).
   */
  roleIds?: string[];
}

/**
 * `PUT …/accounts/{accountId}/roles` — the account's whole set of Team Portal roles, replaced.
 *
 * Refused 404 for an unknown account, 403 `own-account` for the caller's own, 422
 * `advisor-account`, `unknown-role`, `permission-not-held` and `last-role-manager` (survey-api's
 * spec §6.2, in that order). An unchanged set is a no-op with no event, and this app sends none.
 * A changed one **revokes every session the account holds** and writes `role_changed`
 * `{ from, to }` (role codes), so a signed-in holder is signed out and signs in again. Answers
 * 204 (the role-model UI design's §9, item 7); the card re-reads.
 *
 * A Team Portal seat is made as one with its roles (`POST /accounts`); this changes them after.
 * survey-api no longer turns an account into the other kind (SR-411).
 */
export interface ISetAccountRoles {
  roleIds: string[];
}

/** `status` of an IdP invitation — survey-api's `IdpInvitationStatuses`. **Live** is `queued`,
 *  or `pending` before `expiresAt`: one live invitation per account and one per address. A
 *  spelling this app does not know is drawn as sent. */
export type IdpInvitationStatus =
  | 'queued'
  | 'pending'
  | 'accepted'
  | 'superseded'
  | 'withheld'
  | 'failed'
  | 'expired';

/** `source` — what caused it, survey-api's `IdpInvitationSources` (IDP §4). */
export type IdpInvitationSource =
  | 'staff'
  | 'seat'
  | 'registration'
  | 'request'
  | 'contact'
  | 'bootstrap';

/** `reason` — why one is `withheld` or `failed`, survey-api's `IdpInvitationReasons` (§3.3). */
export type IdpInvitationReason =
  | 'not_allowlisted'
  | 'account_disabled'
  | 'idp_rejected'
  | 'idp_not_configured'
  | 'address_taken';

/**
 * One invitation to sign in at the IdP — `AdminIdpInvitationDto`, every column but the IdP's own
 * id. `GET …/accounts/{accountId}/idp-invitations` answers them newest first (IDP §5).
 *
 * `address` is what the IdP invites — the account's address, or the `deliverTo` it was sent to;
 * `intended` the account's address when it was made. `redeemUrl` is the IdP's link on a live
 * `pending` row and null on every other: no bearer link — only the holder of `address` can
 * redeem it — but on dev and UAT it goes to that holder alone, the tester, never to the advisor
 * (§9). `expiresAt` is null until the IdP made it.
 */
export interface IAdminIdpInvitation {
  invitationId: string;
  accountId: string;
  address: string;
  intended: string;
  source: IdpInvitationSource | (string & {});
  /** The member of staff who caused it, for `staff` and `seat`. */
  requestedByAccountId: Nullable<string>;
  status: IdpInvitationStatus | (string & {});
  reason: Nullable<IdpInvitationReason | (string & {})>;
  redeemUrl: Nullable<string>;
  expiresAt: Nullable<string>;
  createdAt: string;
  /** When it stopped being live. */
  decidedAt: Nullable<string>;
}

/**
 * `POST …/accounts/{accountId}/idp-invitations` — staff invite the account to sign in (IDP §5).
 * 202 `{ invitationId }`: the row is `queued` and survey-api's worker asks the IdP. A live one at
 * the same address is **re-sent** rather than replaced; one at another address supersedes it.
 *
 * In survey-api's order: a named `deliverTo` judged on its own (400); an unknown account (404);
 * the caller's own (403 `own-account`); a Team Portal account and a caller without `roles.manage`
 * (a bodiless 403); a permission the caller lacks (422 `permission-not-held`); a disabled account
 * (409 `account-disabled`); a linked one (409 `idp-already-linked`); the account's own address
 * outside the allowlist with no `deliverTo` (400 `missing`); an address another account holds
 * (409 `idp-address-taken`).
 */
export type IInviteToIdp = IDeliverTo;

/** The 202's body. */
export interface IIdpInvitationQueued {
  invitationId: string;
}

/**
 * `PUT /api/v1/admin/accounts/{accountId}/email` — staff set an advisor's address (SR-416,
 * survey-api's B5-13), for an advisor who lost the old mailbox: nothing is asked of the owner,
 * and the address is not proved — it is unconfirmed until its owner opens the confirmation link
 * survey-api mails it. It is where mail goes, and moves nothing at the IdP (IDP D12).
 *
 * In survey-api's order: `deliverTo` judged against `email` (400, the ValidationProblem every
 * inbox refusal is); `email` not one plain address (400 `invalid-body`, a problem at `email`); an
 * unknown account (an empty 404); **a Team Portal account (422 `staff-account`)** — staff set only
 * an advisor's address, so a Team Portal account's mail never moves to a mailbox another member
 * of staff chose; the address the account has already, in any case (204, nothing done); another
 * account's (409 `email-taken`); else 204. No own-account or outranks check exists, and an
 * advisor target needs none.
 *
 * What the 204 does not say: **every session of the account is revoked** and every outstanding
 * link retired, `email_changed {from, to, reason}` is written, and three mails go out, each to `deliverTo`
 * where one is named — a notice to the old address, one to the new, and an address confirmation
 * (`/email/verify#token=`, seven days).
 */
export interface ISetAccountEmail extends IDeliverTo {
  email: string;
  /** Why staff change it (SR-487, PFR §7), required: absent or another value is 400 on `reason`
   *  before any read. Carried on `email_changed {from, to, reason}`. */
  reason: EmailChangeReason;
}

/** Why staff change a login address (`EmailChangeReasons`): the account's own correction, or the
 *  advisor moved firm — which changes nothing else until SR-256 rules on it. */
export type EmailChangeReason = 'account_update' | 'firm_change';

/**
 * `PUT /api/v1/admin/accounts/{accountId}/status` — staff disable an account, or make it active
 * again (SR-442, survey-api's account-disablement design). `accounts.manage`, and `roles.manage`
 * as well for a Team Portal account.
 *
 * In survey-api's order: the body (400 `invalid-body`, a problem at `status` or at `reason`); an
 * unknown account (an empty 404); the caller's own account (403 `own-account`, with a body); a
 * Team Portal account and a caller without `roles.manage` (a **bodiless** 403, as `POST /accounts`
 * answers it); an account holding a permission the caller lacks (422 `permission-not-held`, D5);
 * then the write — 204, whether or not the status changed.
 *
 * What the 204 does not say: **disabling revokes every session the account holds**, and while it
 * is disabled nothing signs in as it and no invitation, address-confirmation or address-change
 * link of its works — the links are kept, not spent, and work again once it is active.
 * **Reactivating restores no session**: its owner signs in through the IdP as before.
 * Nothing the account did is removed or reassigned. Either change writes `status_changed`
 * `{from, to, reason}`; a write that changes nothing writes nothing. No mail goes out.
 */
export interface ISetAccountStatus {
  status: AccountStatus;
  /** Why — kept on the `status_changed` event alone. At most 500 characters, survey-api's bound,
   *  said beside the box and never enforced here. Left out when blank. */
  reason?: string;
}
